import { useAssistantStream } from '@/components/assistant/useAssistantStream';
import { AssistantFab, AssistantPanel } from '@/components/assistant/AssistantChat';

const SUGGESTIONS = [
  'Summarise this ticket',
  'What should I do next?',
  'Draft a reply to the customer',
] as const;

/**
 * Collapsible AI helper for the ticket detail page, with a pop-out modal for a fuller view.
 * Streams a Claude reply (via the server's Foundry-backed `/api/assistant/stream` SSE
 * endpoint) about the open ticket. The server fetches authoritative ticket context with the
 * user's impersonation, so the panel only needs the ticket id.
 */
export function TicketAssistant({ ticketId }: { ticketId: number }) {
  const chat = useAssistantStream({
    endpoint: '/api/assistant/stream',
    body: () => ({ ticketId }),
    resetKey: ticketId,
  });

  return (
    <AssistantPanel
      chat={chat}
      suggestions={SUGGESTIONS}
      suggestionsLabel="Ask about this ticket:"
      placeholder="Ask about this ticket…"
    />
  );
}

const QUEUE_SUGGESTIONS = {
  me: ['What should I work on next?', 'Which of my tickets are going stale?', 'Summarise my queue'],
  unassigned: ['What needs assigning first?', 'Anything urgent in the queue?', 'Summarise the queue by client'],
  all: ['What needs attention first?', 'Which tickets are overdue?', 'Summarise open tickets'],
} as const;

/**
 * Floating Engineering AI on the ticket lists (My Tickets, Queues): the counterpart of the Sales pipeline
 * and Projects portfolio chats. The server reads the open tickets behind the page (`scope`) and computes the
 * totals; only the scope is sent from here.
 */
export function TicketQueueAssistant({ scope }: { scope: 'me' | 'unassigned' | 'all' }) {
  const chat = useAssistantStream({
    endpoint: '/api/assistant/tickets/stream',
    body: () => ({ scope }),
    resetKey: scope,
  });

  return (
    <AssistantFab
      chat={chat}
      title="Engineering Assistant"
      label="Ask Cosmo"
      suggestions={QUEUE_SUGGESTIONS[scope]}
      suggestionsLabel={scope === 'unassigned' ? 'Ask about the queue:' : 'Ask about your tickets:'}
      placeholder={scope === 'unassigned' ? 'Ask about the queue…' : 'Ask about your tickets…'}
    />
  );
}
